/* -----------------------------------------------------------------------------------------------------
  - Created: [30-01-2025] by [SREERAJ]
  - Modified: [21-04-2025] by [SREERAJ]
  - Description:  - Description: This file contains Routing with pages.
  eg: hasPrivilege(privileges, "Work site", 1, 2), //MODULENAME, submodule_id,Privilege Id (Action Id)
------------------------------------------------------------------------------------------------------- */

import React from 'react'
import { Route, Routes } from 'react-router-dom'
import { useSelector } from 'react-redux'

// Layouts and Components
import Layout from '../Pages/Layout/Layout'
import ProtectedRoute from './ProtectedRoute.jsx'
import Loader from '../Components/Loader/Loader.jsx'
import NotFound from '../Pages/NotFound/NotFound.jsx'
import WelcomePage from '../Pages/AuthPage/WelcomePage/WelcomePage.jsx'
import LoginPage from '../Pages/AuthPage/LoginPage/LoginPage.jsx'

// Dashboard
import Dashboard from '../Pages/Dashboard/Dashboard'

// Worksite Management
import ProjectManagementTable from '../Pages/WorkSites/Projects/ProjectManagementTable/ProjectManagementTable.jsx'
import WorksiteTable from '../Pages/WorkSites/Sites/WorksiteTable/WorksiteTable.jsx'
import ScheduleTable from '../Pages/WorkSites/Schedule/ScheduleTable/ScheduleTable.jsx'
import ScheduleDetails from '../Pages/WorkSites/Schedule/ScheduleDetails/ScheduleDetails.jsx'
import CompletedScheduleTable from '../Pages/WorkSites/CompletedSchedule/CompletedScheduleTable/CompletedScheduleTable.jsx'
import CompletedScheduleReportTable from '../Pages/WorkSites/CompletedScheduleReports/CompletedScheduleReportTable/CompletedScheduleReportTable.jsx'
import MyScheduleTable from '../Pages/WorkSites/MySchedules/MyScheduleTable/MyScheduleTable.jsx'
import BinReportTable from '../Pages/WorkSites/Bin/BinReportTable/BinReportTable.jsx'
import MyBinReportTable from '../Pages/WorkSites/Bin/MyBinReports/MyBinReportTable/MyBinReportTable.jsx'
import FSECalendarView from '../Pages/WorkSites/FSECalendarView/FSECalendarView.jsx'

// Resource Management
import EquipmentTable from '../Pages/Resources/Equipment/EquipmentTable/EquipmentTable.jsx'
import WorkforceTable from '../Pages/Resources/Workforce/WorkforceTable/WorkforceTable.jsx'
import WorkforceForm2 from '../Pages/Resources/Workforce/WorkforceForm/WorkforceForm2.jsx'
import WorkforceFormView from '../Pages/Resources/Workforce/WorkforceView/WorkforceFormView.jsx'
import TechnologyTable from '../Pages/Resources/Technology/TechnologyTable/TechnologyTable.jsx'

// Reports
import ProjectReportTable from '../Pages/Reports/ProjectReport/ProjectReportTable/ProjectReportTable.jsx'
import MonthlyReportTable from '../Pages/Reports/MonthlyRepport/MonthlyReportTable/MonthlyReportTable.jsx'
import WorkforceCombinedReportTable from '../Pages/Reports/WorkforceCombinedReport/WorkforceCombinedReportTable/WorkforceCombinedReportTable.jsx'
import EquipmentReportTable from '../Pages/Reports/EquipmentReport/EquipmentReportTable/EquipmentReportTable.jsx'
import WorksiteReportTable from '../Pages/Reports/WorksiteReport/WorksiteReportTable/WorksiteReportTable.jsx'
import InvoiceReportTable from '../Pages/Reports/InvoiceReport/InvoiceReportTable/InvoiceReportTable.jsx'
import FutureScheduleTable from '../Pages/Reports/FutureScheduleReport/FutureScheduleTable/FutureScheduleTable.jsx'
import EmployeeUtilizationTable from '../Pages/Reports/EmployeeUtilizationReport/EmployeeUtilizationTable/EmployeeUtilizationTable.jsx'
import EquipmentUtilizationTable from '../Pages/Reports/EquipmentUtilizationReport/EquipmentUtilizationTable/EquipmentUtilizationTable.jsx'

// Finance
import ScheduleSubmissionTable from '../Pages/Finance/ScheduleSubmissions/ScheduleSubmissionTable/ScheduleSubmissionTable.jsx'
import InvoiceTable from '../Pages/Finance/Invoices/InvoiceTable/InvoiceTable.jsx'

// Leave Management
import LeaveRequestsTable from '../Pages/Management/LeaveRequests/LeaveRequestsTable/LeaveRequestsTable.jsx'
import CompensationLeaveTable from '../Pages/Management/Compensation Leave/CompensationLeaveTable/CompensationLeaveTable.jsx'
import LeaveTracker from '../Pages/Settings/Weekend/LeaveCalendar/LeaveCalendar.jsx'

// Settings & Administration
import RoleTable from '../Pages/Settings/Roles/RolesTable/RoleTable.jsx'
import DesignationTable from '../Pages/Settings/Designation/DesignationTable/DesignationTable.jsx'
import ClassificationTable from '../Pages/Settings/Classification/ClassificationTable/ClassificationTable.jsx'
import LeaveTypeTable from '../Pages/Settings/LeaveTypes/LeaveTypeTable/LeaveTypeTable.jsx'
import CertificationTable from '../Pages/Settings/Certification/CertificationTable/CertificationTable.jsx'
import CalendarView from '../Pages/Settings/Weekend/Calendar/CalendarView'
import RolesPermissions from '../Pages/Settings/RolesPermissions/RolesPermissions.jsx'
import EmailNotificationAssign from '../Pages/Settings/EmailNotificationAssign/EmailNotificationAssign.jsx'

// Others
import AuditLogs from '../Pages/AuditLog/AuditLogs/AuditLogs.jsx'
import NotificationList from '../Pages/NotificationList/NotificationList.jsx'

// Utilities
import { hasPrivilege } from '../utils/privileges.js'
import { getUserRoleFlags } from '../utils/userRoleUtils.js'


function Routers() {
    const privileges = useSelector((state) => state?.privileges?.privileges)

    // Extract user role 
    const { isSuperAdmin, isSupervisor, isManagement } = getUserRoleFlags()

    //  render routes based on privileges
    const renderRoute = (condition, Component) => condition ? Component : <Loader />

    return (
        <Routes>
            {/* ------------------------ PUBLIC ROUTES ------------------------ */}
            <Route path="/" element={<WelcomePage />} />
            <Route path="/login" element={<LoginPage />} />

            {/* ---------------------- PROTECTED ROUTES ----------------------- */}
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <Layout />
                    </ProtectedRoute>
                }
            >

                {/* -------------------- DASHBOARD -------------------- */}
                <Route path="dashboard/dashboard/overview" element={<Dashboard />} />


                {/* -------------------- WORKSITE MODULE -------------------- */}
                <Route path="worksites/projects/overview" element={renderRoute(
                    hasPrivilege(privileges, "Work site", 1, 2),
                    <ProjectManagementTable />
                )} />

                <Route path="worksites/worksites/overview" element={renderRoute(
                    hasPrivilege(privileges, "Work site", 2, 2),
                    <WorksiteTable />
                )} />

                <Route path="worksites/schedule/overview" element={renderRoute(
                    hasPrivilege(privileges, "Work site", 3, 2),
                    <ScheduleTable />
                )} />

                {/* Supervisor: My Schedule View */}
                <Route path="worksites/my-schedules/overview" element={renderRoute(
                    isSupervisor,
                    <MyScheduleTable />
                )} />


                {/* -------------------- RESOURCES MODULE -------------------- */}
                <Route path="resources/equipment/list" element={renderRoute(
                    hasPrivilege(privileges, "Resources", 5, 2),
                    <EquipmentTable />
                )} />
                <Route path="resources/technologies/list" element={renderRoute(
                    hasPrivilege(privileges, "Resources", 4, 2),
                    <TechnologyTable />
                )} />
                <Route path="resources/workforce/list" element={renderRoute(
                    hasPrivilege(privileges, "Resources", 6, 2),
                    <WorkforceTable />
                )} />
                <Route path="resources/workforce/add" element={renderRoute(
                    hasPrivilege(privileges, "Resources", 6, 3),
                    <WorkforceForm2 />
                )} />
                <Route path="resources/workforce/view/:userID" element={renderRoute(
                    hasPrivilege(privileges, "Resources", 6, 4),
                    <WorkforceFormView />
                )} />
                <Route path="resources/workforce/edit/:userRole/:userID" element={renderRoute(
                    hasPrivilege(privileges, "Resources", 6, 5),
                    <WorkforceForm2 />
                )} />


                {/* -------------------- REPORTS MODULE -------------------- */}
                <Route path="reports/reports/project-management" element={renderRoute(
                    hasPrivilege(privileges, "Reports", 24, 2),
                    <ProjectReportTable />
                )} />
                {/* <Route path="reports/reports/monthly-report" element={renderRoute(
                    hasPrivilege(privileges, "Reports", "Monthly", 2),
                    <MonthlyReportTable />
                )} /> */}
                <Route path="reports/reports/employee-management" element={renderRoute(
                    hasPrivilege(privileges, "Reports", 23, 2),
                    <WorkforceCombinedReportTable />
                )} />
                <Route path="reports/reports/equipment-report" element={renderRoute(
                    hasPrivilege(privileges, "Reports", 25, 2),
                    <EquipmentReportTable />
                )} />
                <Route path="reports/reports/worksite-report" element={renderRoute(
                    hasPrivilege(privileges, "Reports", 26, 2),
                    <WorksiteReportTable />
                )} />
                <Route path="reports/reports/invoice-report" element={renderRoute(
                    hasPrivilege(privileges, "Reports", 27, 2),
                    <InvoiceReportTable />
                )} />
                <Route path="reports/employee/schedule" element={renderRoute(
                    hasPrivilege(privileges, "Reports", 28, 2),
                    <FutureScheduleTable />
                )} />
                <Route path="reports/employee/utilization" element={renderRoute(
                    hasPrivilege(privileges, "Reports", 30, 2),
                    <EmployeeUtilizationTable />
                )} />
                <Route path="reports/equipment/utilization" element={renderRoute(
                    hasPrivilege(privileges, "Reports", 31, 2),
                    <EquipmentUtilizationTable />
                )} />


                {/* -------------------- AUDIT LOGS -------------------- */}
                <Route path="/auditlogs/overview" element={renderRoute(
                    isSuperAdmin,
                    <AuditLogs />
                )} />


                {/* -------------------- SETTINGS MODULE -------------------- */}
                <Route path="settings/role/list" element={renderRoute(
                    hasPrivilege(privileges, "Settings", 10, 2),
                    <RoleTable />
                )} />
                <Route path="settings/designation/list" element={renderRoute(
                    hasPrivilege(privileges, "Settings", 11, 2),
                    <DesignationTable />
                )} />
                <Route path="settings/classification/list" element={renderRoute(
                    hasPrivilege(privileges, "Settings", 10, 2),
                    <ClassificationTable />
                )} />
                <Route path="settings/leaveType/list" element={renderRoute(
                    hasPrivilege(privileges, "Settings", 12, 2),
                    <LeaveTypeTable />
                )} />
                <Route path="settings/category/list" element={renderRoute(
                    hasPrivilege(privileges, "Settings", 13, 2),
                    <CertificationTable />
                )} />
                <Route path="settings/holiday/view" element={renderRoute(
                    hasPrivilege(privileges, "Settings", 14, 2),
                    <CalendarView />
                )} />
                <Route path="/settings/RolesPermissions" element={renderRoute(
                    hasPrivilege(privileges, "Settings", 15, 2),
                    <RolesPermissions />
                )} />
                <Route path="/settings/EmailNotificationAssign" element={renderRoute(
                    hasPrivilege(privileges, "Settings", 15, 2),
                    <EmailNotificationAssign />
                )} />


                {/* -------------------- LEAVE MANAGEMENT -------------------- */}
                <Route path="leave/leave/list" element={renderRoute(
                    hasPrivilege(privileges, "Leave", 16, 2),
                    <LeaveRequestsTable />
                )} />
                <Route path="leave/leave/calender" element={renderRoute(
                    hasPrivilege(privileges, "Leave", 17, 2),
                    <LeaveTracker />
                )} />
                <Route path="leave/compensation/Leave" element={renderRoute(
                    hasPrivilege(privileges, "Leave", 33, 2),
                    <CompensationLeaveTable />
                )} />


                {/* -------------------- BIN & SCHEDULE REPORTS -------------------- */}
                <Route path="schedule/bin-report/list" element={renderRoute(
                    hasPrivilege(privileges, "Schedule", 32, 2),
                    <BinReportTable />
                )} />
                <Route path="worksites/schedule/:scheduleId/bin-report/list" element={renderRoute(
                    hasPrivilege(privileges, "Schedule", 32, 2),
                    <MyBinReportTable />
                )} />
                <Route path="schedule/report/list" element={renderRoute(
                    hasPrivilege(privileges, "Schedule", 18, 2),
                    <CompletedScheduleReportTable />
                )} />
                <Route path="schedule/kpi/list" element={renderRoute(
                    hasPrivilege(privileges, "Schedule", 19, 2),
                    <CompletedScheduleTable />
                )} />


                {/* -------------------- FINANCE -------------------- */}
                <Route path="finance/Finance/list" element={renderRoute(
                    hasPrivilege(privileges, "Finances", 20, 2),
                    <ScheduleSubmissionTable />
                )} />
                <Route path="finance/invoice/list" element={renderRoute(
                    hasPrivilege(privileges, "Finances", 21, 2),
                    <InvoiceTable />
                )} />


                {/* -------------------- FSE CALENDAR VIEW -------------------- */}
                <Route path="schedule/fse-calendar/view" element={renderRoute(
                    isSupervisor || isManagement,
                    <FSECalendarView />
                )} />


                {/* -------------------- PROFILE & NOTIFICATIONS -------------------- */}
                <Route path="profile/workforce/edit/:userRole/:userID" element={renderRoute(
                    true, // Profile edit allowed without privilege
                    <WorkforceForm2 />
                )} />
                <Route path="notification/list" element={<NotificationList />} />

                {/* Schedule Details */}
                <Route path="worksites/schedule/:scheduleId" element={<ScheduleDetails />} />

                {/* -------------------- 404 CATCH-ALL -------------------- */}
                <Route path="*" element={<NotFound />} />

            </Route>
        </Routes>
    )
}

export default Routers
